import React from 'react';
import { useSearchParams } from 'react-router-dom';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import LeadForm from '../../components/systems/LeadForm';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { getCtaCopy, PLUTIO_SCHEDULER_URL } from '../../data/systemsCatalog';
import { Check, ExternalLink, Calendar } from 'lucide-react';

export default function BookDemo() {
  const [params] = useSearchParams();
  const slug = params.get('service');
  const { getService } = useSystemsCatalog();
  const svc = slug ? getService(slug) : null;
  const copy = getCtaCopy(svc);

  const bullets = copy.isConsultation
    ? [
        'Professional advisory for individuals, teams, and organizations',
        'Scoping, sizing, and transparent price discussion',
        'Actionable roadmap delivered after the call',
      ]
    : [
        'Professional IT management for individuals and businesses',
        'Infrastructure, cloud, and security support',
        'Proactive monitoring and reliable technical assistance',
      ];

  const heroEyebrow = copy.isConsultation ? 'Book a Consultation' : 'Book a Demo';
  const heroAccent = copy.isConsultation ? 'Schedule a consultation' : 'Schedule a demo';
  const heroTitleLead = copy.isConsultation ? 'Let\u2019s plan' : 'See ZHELTA Systems';
  const heroTitleAccent = copy.isConsultation ? 'your next move.' : copy.heroTitle;

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path="/systems/book-demo"
        title={`${heroEyebrow} — ZHELTA Systems${svc ? ` (${svc.name})` : ''}`}
        description={copy.heroSubtitle}
      />
      <SystemsHeader />
      <main className="py-16 md:py-20">
        <div className="zh-container max-w-6xl">
          {/* Hero */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
            <div>
              <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">{heroEyebrow}</span>
              <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
                {heroTitleLead}<br />
                <span className="text-[#f5a623]">{heroTitleAccent}</span>
              </h1>
              <p className="mt-6 text-[17px] text-gray-600 leading-relaxed">
                {copy.heroSubtitle}
              </p>
              {svc && (
                <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100 text-[12px] text-amber-900 font-semibold" data-testid="book-demo-service-tag">
                  Interested in: {svc.name}
                </div>
              )}
              <ul className="mt-7 space-y-3">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-gray-700">
                    <Check className="w-4 h-4 text-[#f5a623] mt-1 flex-shrink-0" strokeWidth={3} /><span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[13px] text-gray-500">Trusted technology partner for individuals, businesses, and organizations.</p>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-[0_4px_24px_-8px_rgba(0,0,0,0.06)]">
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-[#fafafa]">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#c9a227]" strokeWidth={2.2} />
                  <span className="text-[13px] font-bold text-gray-900">{heroAccent}</span>
                </div>
                <a
                  href={PLUTIO_SCHEDULER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-demo-open-new-tab"
                  className="inline-flex items-center gap-1 text-[12px] font-semibold text-gray-600 hover:text-[#f5a623] transition-colors"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <iframe
                src={PLUTIO_SCHEDULER_URL}
                title={heroAccent}
                loading="lazy"
                data-testid="book-demo-plutio-iframe"
                className="w-full min-h-[720px] border-0 block"
                allow="clipboard-read; clipboard-write; camera; microphone"
              />
            </div>
          </div>

          {/* Fallback lead form for users who can't use Plutio */}
          <div className="mt-16 pt-10 border-t border-gray-100">
            <div className="max-w-3xl mx-auto text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Prefer we reach out to you?</h2>
              <p className="mt-2 text-gray-600 text-[15px]">Share your details and a specialist will contact you within 1 business day.</p>
            </div>
            <div className="max-w-3xl mx-auto">
              <LeadForm
                kind={copy.isConsultation ? 'consultation' : 'book-demo'}
                slug={slug}
                fields={['company', 'phone', 'preferred_time']}
                ctaLabel={copy.isConsultation ? 'Request consultation' : 'Request demo'}
                successTitle={copy.isConsultation ? 'Consultation request received' : 'Demo request received'}
                testIdPrefix="book-demo"
              />
            </div>
          </div>
        </div>
      </main>
      <SystemsFooter />
    </div>
  );
}
